import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { MessageCircle } from "lucide-react";
import { Seo } from "../components/Seo";
import { Reveal, SectionHeading, Overline } from "../components/Reveal";
import { api, WHATSAPP_URL } from "../lib/api";

const JOURNEYS = [
  { id: "experience", label: "Experience Our VR", desc: "For customers, families and groups planning a visit." },
  { id: "partner", label: "Partner With Us", desc: "For studios, distributors, venues and investors." },
  { id: "build", label: "Build an XR Project", desc: "For brands and institutions producing immersive content." },
];

const FIELDS = [
  ["name", "Name", "text", true], ["company", "Company", "text", false],
  ["email", "Email", "email", true], ["phone", "Phone", "tel", false],
  ["country", "Country", "text", false], ["city", "City", "text", false],
];

const useContactForm = (journey) => {
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.enquire({ ...form, type: journey });
      setDone(true);
      toast.success("Message received. We'll respond shortly.");
    } catch {
      toast.error("Submission failed. Please check required fields.");
    } finally {
      setBusy(false);
    }
  };

  return { form, setForm, set, busy, done, setDone, submit };
};

const JourneyPicker = ({ journey, onPick }) => (
  <>
    {JOURNEYS.map((j) => (
      <button key={j.id} onClick={() => onPick(j.id)} data-testid={`journey-${j.id}`}
        className={`w-full text-left rounded-2xl border p-7 transition-all duration-400 ${journey === j.id ? "border-white/35 bg-white/[0.04]" : "border-white/10 hover:border-white/25"}`}>
        <p className="font-serif text-xl md:text-2xl tracking-tight mb-2">{j.label}</p>
        <p className="text-sm text-zinc-500 font-light">{j.desc}</p>
      </button>
    ))}
  </>
);

const SuccessCard = ({ name, journeyLabel }) => (
  <Reveal className="rounded-2xl border border-white/10 bg-white/[0.02] p-12 text-center h-full flex flex-col items-center justify-center" data-testid="contact-form-success">
    <h3 className="font-serif text-3xl mb-4">Message received.</h3>
    <p className="text-zinc-400 font-light mb-8 max-w-sm">Thank you, {name?.split(" ")[0] || "friend"}. Our team will respond shortly. Want an instant answer?</p>
    <a href={WHATSAPP_URL(`Hi Vrova, I just submitted a "${journeyLabel}" enquiry via vrova.in.`)} target="_blank" rel="noreferrer"
      data-testid="contact-success-whatsapp"
      className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full bg-white text-black text-sm font-medium hover:bg-zinc-200 transition-colors">
      <MessageCircle size={16} /> Continue on WhatsApp
    </a>
  </Reveal>
);

const ContactForm = ({ journeyLabel, form, set, setForm, busy, onSubmit }) => (
  <form onSubmit={onSubmit} data-testid="contact-form" className="rounded-2xl border border-white/10 bg-white/[0.02] backdrop-blur-xl p-8 md:p-12 grid grid-cols-1 md:grid-cols-2 gap-6">
    <p className="md:col-span-2 text-xs font-mono uppercase tracking-[0.2em] text-zinc-500">Enquiry type — {journeyLabel}</p>
    {FIELDS.map(([key, label, type, req]) => (
      <div key={key}>
        <label htmlFor={`contact-${key}`}>{label}{req ? " *" : ""}</label>
        <input id={`contact-${key}`} data-testid={`contact-field-${key}`} type={type} required={req} value={form[key] || ""} onChange={set(key)} />
      </div>
    ))}
    <div className="md:col-span-2">
      <label htmlFor="contact-message">Message *</label>
      <textarea id="contact-message" data-testid="contact-field-message" rows={5} required value={form.message || ""} onChange={set("message")} />
    </div>
    <div className="md:col-span-2">
      <label htmlFor="contact-file">Attachment (optional)</label>
      <input id="contact-file" data-testid="contact-field-file" type="file"
        onChange={(e) => setForm((f) => ({ ...f, attachment_name: e.target.files?.[0]?.name || "" }))}
        className="!p-3 file:mr-4 file:rounded-full file:border-0 file:bg-white file:text-black file:px-4 file:py-1.5 file:text-xs file:font-medium" />
      <p className="text-[11px] text-zinc-600 mt-2 font-mono">Large files? Share a Drive/Dropbox link in your message instead.</p>
    </div>
    <div className="md:col-span-2">
      <button type="submit" data-testid="contact-form-submit" disabled={busy}
        className="w-full md:w-auto px-10 py-4 rounded-full bg-white text-black text-sm font-medium hover:bg-zinc-200 transition-colors disabled:opacity-50">
        {busy ? "Sending…" : "Send Message"}
      </button>
    </div>
  </form>
);

export default function Contact() {
  const [params] = useSearchParams();
  const [journey, setJourney] = useState("experience");
  const { form, setForm, set, busy, done, setDone, submit } = useContactForm(journey);

  useEffect(() => {
    const t = params.get("type");
    if (t && JOURNEYS.some((j) => j.id === t)) setJourney(t);
  }, [params]);

  const active = JOURNEYS.find((j) => j.id === journey);

  return (
    <main className="pt-40 pb-28">
      <Seo title="Contact" path="/contact"
        description="Contact Vrova — experience our VR, partner with us, or build an XR project. WhatsApp +91 62811 28057." />
      <div className="max-w-[1440px] mx-auto px-6 md:px-10">
        <SectionHeading overline="Contact" title={<>Start a <span className="italic text-zinc-400">Conversation.</span></>} />
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-14 mt-16">
          <div className="lg:col-span-5 space-y-4">
            <JourneyPicker journey={journey} onPick={(id) => { setJourney(id); setDone(false); }} />
            <Reveal className="pt-6">
              <Overline className="mb-4">Prefer to Talk?</Overline>
              <a href={WHATSAPP_URL()} target="_blank" rel="noreferrer" data-testid="contact-whatsapp-cta"
                className="inline-flex items-center gap-3 px-7 py-4 rounded-full border border-white/20 text-sm hover:bg-white/10 transition-colors">
                <MessageCircle size={17} /> WhatsApp: +91 62811 28057
              </a>
              <p className="text-sm text-zinc-500 mt-6 font-light">vrova.in · Hyderabad, India</p>
            </Reveal>
          </div>
          <div className="lg:col-span-7">
            {done ? (
              <SuccessCard name={form.name} journeyLabel={active.label} />
            ) : (
              <ContactForm journeyLabel={active.label} form={form} set={set} setForm={setForm} busy={busy} onSubmit={submit} />
            )}
          </div>
        </div>
      </div>
    </main>
  );
}
